import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";

const TITLES = {
  EXACT_DUPLICATE: "This exact file is already imported",
  OVERLAP: "Overlapping statement detected",
  POSSIBLE_DUPLICATE: "A statement already exists for this platform and month",
};

export default function CommissionConflictDialog({ conflict, onClose, onResolve, loading }) {
  const [mode, setMode] = useState("");
  const [reason, setReason] = useState("");
  const [account, setAccount] = useState("");
  const [picked, setPicked] = useState({});
  if (!conflict) return null;
  const replaceable = conflict.code === "OVERLAP" ? conflict.overlapping || [] : (conflict.same_month || []).map((r) => r.commission_id);
  const ids = conflict.code === "OVERLAP" ? replaceable : replaceable.filter((id) => picked[id]);
  const submit = () => onResolve(mode === "replace"
    ? { replace_commission_ids: ids, replace_reason: reason }
    : { confirm_supplementary: true, supplementary_note: reason, merchant_account: account });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-xl" data-testid="commission-conflict-dialog">
        <DialogHeader>
          <DialogTitle>{TITLES[conflict.code] || "Possible duplicate"}</DialogTitle>
          <DialogDescription>{conflict.message}</DialogDescription>
        </DialogHeader>
        <div className="space-y-3 text-sm">
          <div className="border rounded p-2 max-h-40 overflow-auto">
            {(conflict.same_month || []).map((r) => (
              <label key={r.commission_id} className="flex items-center gap-2 py-1">
                {conflict.code === "POSSIBLE_DUPLICATE" && mode === "replace" && (
                  <Checkbox checked={!!picked[r.commission_id]} onCheckedChange={(v) => setPicked({ ...picked, [r.commission_id]: !!v })} data-testid={`conflict-pick-${r.commission_id}`} />
                )}
                <span>{r.original_filename || r.commission_id} {r.period_start ? `(${r.period_start} → ${r.period_end})` : ""} {r.merchant_account ? `· ${r.merchant_account}` : ""}</span>
                {(conflict.overlapping || []).includes(r.commission_id) && <span className="text-red-700 text-xs">overlaps{conflict.transaction_overlap?.[r.commission_id] ? ` (${conflict.transaction_overlap[r.commission_id]} transactions)` : ""}</span>}
              </label>
            ))}
          </div>
          {conflict.code !== "EXACT_DUPLICATE" && (
            <div className="flex gap-2">
              <Button size="sm" variant={mode === "replace" ? "default" : "outline"} onClick={() => setMode("replace")} data-testid="conflict-mode-replace">Replace existing statement(s)</Button>
              {conflict.code === "POSSIBLE_DUPLICATE" && <Button size="sm" variant={mode === "separate" ? "default" : "outline"} onClick={() => setMode("separate")} data-testid="conflict-mode-separate">It's a separate statement</Button>}
            </div>
          )}
          {mode === "separate" && <div><Label>Merchant account / statement label (optional)</Label><Input value={account} onChange={(e) => setAccount(e.target.value)} data-testid="conflict-account" /></div>}
          {mode && <div><Label>{mode === "replace" ? "Reason for replacement *" : "Why is this separate (weekly, supplementary…)? *"}</Label><Input value={reason} onChange={(e) => setReason(e.target.value)} data-testid="conflict-reason" /></div>}
          {mode === "replace" && <p className="text-xs text-muted-foreground">Replaced statements are kept as SUPERSEDED in the audit history and stop counting in calculations.</p>}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Cancel</Button>
          {mode && <Button onClick={submit} disabled={loading || reason.trim().length < (mode === "replace" ? 5 : 3) || (mode === "replace" && !ids.length)} data-testid="conflict-confirm">Confirm</Button>}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
